import { Trash } from "lucide-react";
import {
  useDeleteController,
  useRecordContext,
  useResourceTranslation,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Confirm } from "@/components/admin/confirm";
import { DeleteButton } from "@/components/admin/delete-button";
import { Button } from "@/components/ui/button";

import type { Company } from "../types";

type CompanyDeleteButtonProps = {
  className?: string;
  size?: "default" | "sm" | "lg" | "icon";
};

// Deleting a company keeps its deals and contacts, with their company cleared. When it
// has any, say how many before deleting and say they were kept afterwards; a company
// with neither deletes in one click as before. LCATOMICZO-61.
export const CompanyDeleteButton = ({
  className,
  size,
}: CompanyDeleteButtonProps) => {
  const record = useRecordContext<Company>();
  const nbDeals = record?.nb_deals ?? 0;
  const nbContacts = record?.nb_contacts ?? 0;

  if (nbDeals === 0 && nbContacts === 0) {
    return <DeleteButton className={className} size={size} />;
  }
  return (
    <ConfirmedCompanyDeleteButton
      className={className}
      size={size}
      nbDeals={nbDeals}
      nbContacts={nbContacts}
    />
  );
};

const ConfirmedCompanyDeleteButton = ({
  className,
  size,
  nbDeals,
  nbContacts,
}: CompanyDeleteButtonProps & { nbDeals: number; nbContacts: number }) => {
  const record = useRecordContext<Company>();
  const translate = useTranslate();
  const [isOpen, setIsOpen] = useState(false);
  const { isPending, handleDelete } = useDeleteController({
    record,
    resource: "companies",
    redirect: "list",
    mutationMode: "undoable",
    successMessage: "resources.companies.notifications.deleted_kept",
  });
  // Same label the kit DeleteButton computes, so both branches read the same.
  const label = useResourceTranslation({
    resourceI18nKey: "resources.companies.action.delete",
    baseI18nKey: "ra.action.delete",
    options: {
      name: translate("resources.companies.forcedCaseName"),
      recordRepresentation: record?.name,
    },
  });

  // Polyglot pluralises one smart_count per string, so each count is its own phrase.
  const linked = [
    nbDeals > 0 &&
      translate("resources.companies.nb_deals", { smart_count: nbDeals }),
    nbContacts > 0 &&
      translate("resources.companies.nb_contacts", {
        smart_count: nbContacts,
      }),
  ]
    .filter(Boolean)
    .join(` ${translate("resources.companies.delete_confirm.and")} `);

  return (
    <>
      <Button
        variant="outline"
        type="button"
        size={size}
        className={className}
        disabled={isPending}
        onClick={() => setIsOpen(true)}
      >
        <Trash />
        {label}
      </Button>
      <Confirm
        isOpen={isOpen}
        loading={isPending}
        confirmColor="warning"
        title="resources.companies.delete_confirm.title"
        titleTranslateOptions={{ name: record?.name ?? "" }}
        content="resources.companies.delete_confirm.content"
        contentTranslateOptions={{ name: record?.name ?? "", linked }}
        onConfirm={() => {
          setIsOpen(false);
          handleDelete();
        }}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};
